import React, {useEffect, useMemo, useState} from 'react';
import {Box} from '@mui/material';
import {FormattedMessage, IntlShape} from 'react-intl';
import Selector from '../../components/shared/nextgen/selector';
import PropertyFilterPopup from '../../components/shared/property-filter-popup';
import {Tag} from '../../components/shared/tag';
import TagWrapper from '../../components/shared/tag-wrapper';
import {PropertyFilter} from '../../features/schedule/types';
import {REmployees} from '../../features/roles/types';
import {Lx} from '../../i18n/consts';

export type TimeBankFilterState = {
    selectedEmployees: PropertyFilter['selectedEmployees'];
};

export const emptyTimeBankFilterState: TimeBankFilterState = {
    selectedEmployees: [],
};

export const normalizeTimeBankFilterState = (filter: Partial<TimeBankFilterState>): TimeBankFilterState => ({
    selectedEmployees: filter.selectedEmployees ?? [],
});

export default function TimeBankFilter(props: {
    filters: TimeBankFilterState;
    employees: REmployees[];
    onChange: (filter: TimeBankFilterState) => Promise<void>;
    disabled: boolean;
    intl: IntlShape;
}) {
    const [current, setCurrent] = useState<TimeBankFilterState>(props.filters);
    const [draft, setDraft] = useState<TimeBankFilterState>(props.filters);
    const employeeItems = useMemo(() => props.employees.map((employee) => ({
        id: employee.id,
        name: employee.name,
        originalId: employee.id,
    })), [props.employees]);

    useEffect(() => {
        setCurrent(props.filters);
        setDraft(props.filters);
    }, [props.filters]);

    const selectedFiltersCount = current.selectedEmployees.length > 0 ? 1 : 0;

    const handleReset = () => {
        setDraft(emptyTimeBankFilterState);
    };

    const handleCancel = () => {
        setDraft(current);
    };

    const handleApply = () => {
        const nextFilters = {
            selectedEmployees: draft.selectedEmployees ?? [],
        };

        setCurrent(nextFilters);
        void props.onChange(nextFilters);
    };

    return (
        <PropertyFilterPopup
            selectedFiltersCount={selectedFiltersCount}
            previewContent={<TimeBankFilterPreview selectedEmployees={current.selectedEmployees} />}
            onReset={handleReset}
            onApply={handleApply}
            onCancel={handleCancel}
            disabled={props.disabled}
        >
            <Selector
                id='time-bank-filter-employees'
                label={Lx.General.EMPLOYEES}
                items={employeeItems}
                name={Lx.General.EMPLOYEES}
                selected={draft.selectedEmployees}
                maxWidth={368}
                setSelectedValues={(values) => setDraft({selectedEmployees: values})}
                intl={props.intl}
                disabled={props.disabled}
            />
        </PropertyFilterPopup>
    );
}

function TimeBankFilterPreview(props: { selectedEmployees: TimeBankFilterState['selectedEmployees'] }) {
    if (props.selectedEmployees.length < 1) {
        return null;
    }

    return (
        <Box display='flex' flexDirection='column' padding='12px 16px' gap='8px' maxWidth={300}>
            <div>
                <FormattedMessage id={Lx.General.EMPLOYEES}/>
            </div>
            <TagWrapper>
                {props.selectedEmployees.map((employee, index) => (
                    <Tag
                        key={`${employee.originalId ?? employee.id}-${index}`}
                        label={employee.name}
                        title={employee.name}
                        showCancelIcon={false}
                    />
                ))}
            </TagWrapper>
        </Box>
    );
}
